import GUI from "lil-gui";
import * as THREE from "three";

import { easeInOutSine } from "../utils/easings";

type AuroraPlaneConfig = {
  x: number;
  y: number;
  z: number;
  rotationX: number;
  rotationY: number;
  scaleX: number;
  scaleY: number;
};

type StateTranslateY = "top" | "default" | "bottom";

type AuroraConfig = {
  camera: {
    fov: number;
    x: number;
    y: number;
    z: number;
    lookAtX: number;
    lookAtY: number;
    lookAtZ: number;
    driftX: number;
    driftY: number;
    driftSpeedX: number;
    driftSpeedY: number;
  };
  sky: {
    topColor: string;
    bottomColor: string;
    offsetY: number;
  };
  gradient: {
    topColorA: string;
    bottomColorA: string;
    topColorB: string;
    bottomColorB: string;
    offsetY: number;
    translateY: number;
    opacity: number;
    fadeBottomStart: number;
    fadeBottomEnd: number;
    fadeTopStart: number;
    fadeTopEnd: number;
    mouseMoveEase: number;
  };
  stars: {
    count: number;
    minRadius: number;
    maxRadius: number;
    verticalOffset: number;
    heightSpread: number;
    depthOffset: number;
    minSize: number;
    maxSize: number;
    twinkleBase: number;
    twinkleRange: number;
    twinkleSpeed: number;
    pointScale: number;
    rotationSpeed: number;
    color: string;
  };
  aurora: {
    colorA: string;
    colorB: string;
    colorC: string;
    opacity: number;
    timeScale: number;
    driftSpeed: number;
    columns: number;
    columnStrength: number;
    veilScaleX: number;
    veilScaleY: number;
    shimmerScaleX: number;
    shimmerScaleY: number;
    shimmerStrength: number;
    lowerFadeStart: number;
    lowerFadeEnd: number;
    upperFadeStart: number;
    upperFadeEnd: number;
    wave1Speed: number;
    wave1Scale: number;
    wave1Strength: number;
    wave2Speed: number;
    wave2Scale: number;
    wave2Strength: number;
    wave3Speed: number;
    wave3Scale: number;
    wave3Strength: number;
    swayAmplitude: number;
    swayFrequency: number;
    swaySpeed: number;
    ribbonStart: number;
    ribbonPeak: number;
    ribbonEnd: number;
    ribbonTop: number;
    edgeFadeLeftStart: number;
    edgeFadeLeftEnd: number;
    edgeFadeRightStart: number;
    edgeFadeRightEnd: number;
    edgeFadeBottomStart: number;
    edgeFadeBottomEnd: number;
    edgeFadeTopStart: number;
    edgeFadeTopEnd: number;
  };
  pageEdgeIntersection: {
    translateYTop: number;
    translateYDefault: number;
    translateYBottom: number;
    state: StateTranslateY;
  };
  planes: {
    planeA: AuroraPlaneConfig;
    planeB: AuroraPlaneConfig;
    planeC: AuroraPlaneConfig;
  };
};

const TARGET_FRAME_MS = 16.67;
const TARGET_FPS = 1000 / TARGET_FRAME_MS;
const PRESET_STORAGE_KEY = "aurora-controls-preset";
const HEADER_LOGO_PLANES_TRANSLATE_DURATION_MS = 400;
const HEADER_LOGO_GRADIENT_TRANSLATE_DURATION_MS = 600;

const defaultConfig: AuroraConfig = {
  camera: {
    fov: 46,
    x: 4.7,
    y: 8.9,
    z: 12,
    lookAtX: 3.3,
    lookAtY: 12.9,
    lookAtZ: -3.4,
    driftX: 0,
    driftY: 0.6,
    driftSpeedX: 0.364,
    driftSpeedY: 0.097,
  },
  sky: {
    topColor: "#000000",
    bottomColor: "#125ca1",
    offsetY: -0.1999,
  },
  gradient: {
    topColorA: "#b8149a",
    bottomColorA: "#d9206a",
    topColorB: "#770938",
    bottomColorB: "#d40217",
    offsetY: -26.2,
    translateY: -1.4,
    opacity: 0.69,
    fadeBottomStart: -24,
    fadeBottomEnd: 16.1,
    fadeTopStart: -10.8,
    fadeTopEnd: 24,
    mouseMoveEase: 0.69,
  },
  stars: {
    count: 200,
    minRadius: 45,
    maxRadius: 90,
    verticalOffset: 8,
    heightSpread: 0.35,
    depthOffset: -20,
    minSize: 0.8,
    maxSize: 3,
    twinkleBase: 0.8,
    twinkleRange: 0.86,
    twinkleSpeed: 1.8,
    pointScale: 112,
    rotationSpeed: 0.01,
    color: "#d9ebff",
  },
  aurora: {
    colorA: "#e9e577",
    colorB: "#c2df72",
    colorC: "#ffee38",
    opacity: 0.98,
    timeScale: 1.66,
    driftSpeed: 0.249,
    columns: 51.7,
    columnStrength: 0.19,
    veilScaleX: 27.8,
    veilScaleY: 7,
    shimmerScaleX: 14.4,
    shimmerScaleY: 2.6,
    shimmerStrength: 0.83,
    lowerFadeStart: 0,
    lowerFadeEnd: 0.72,
    upperFadeStart: 0.1,
    upperFadeEnd: 1,
    wave1Speed: 0.45,
    wave1Scale: 0.26,
    wave1Strength: 2,
    wave2Speed: 0.98,
    wave2Scale: 0.12,
    wave2Strength: 1.4,
    wave3Speed: 0.95,
    wave3Scale: 0.25,
    wave3Strength: 0.75,
    swayAmplitude: 0.03,
    swayFrequency: 10.1,
    swaySpeed: 2.93,
    ribbonStart: 0,
    ribbonPeak: 0.74,
    ribbonEnd: 0.68,
    ribbonTop: 1,
    edgeFadeLeftStart: 0,
    edgeFadeLeftEnd: 0.3,
    edgeFadeRightStart: 0.76,
    edgeFadeRightEnd: 1,
    edgeFadeBottomStart: 0,
    edgeFadeBottomEnd: 0.08,
    edgeFadeTopStart: 0.39,
    edgeFadeTopEnd: 1,
  },
  pageEdgeIntersection: {
    translateYTop: 0,
    translateYDefault: 6,
    translateYBottom: -6,
    state: "default",
  },
  planes: {
    planeA: {
      x: 0,
      y: 3.2,
      z: -26,
      rotationX: 0,
      rotationY: 0,
      scaleX: 1.37,
      scaleY: 1.67,
    },
    planeB: {
      x: -24.1,
      y: -2.4,
      z: -32,
      rotationX: 0,
      rotationY: 0.1384,
      scaleX: 2.41,
      scaleY: 1.72,
    },
    planeC: {
      x: 21.7,
      y: -2.4,
      z: -30,
      rotationX: 0,
      rotationY: -0.28,
      scaleX: 1.87,
      scaleY: 1.47,
    },
  },
};
export class Aurora {
  private readonly params: AuroraConfig = structuredClone(defaultConfig);

  private readonly gui = new GUI({ title: "Aurora Controls" });

  private readonly importInput = document.createElement("input");

  private readonly pageHeaderLogoIntersectionObserver: IntersectionObserver;
  private readonly pageFooterNavIntersectionObserver: IntersectionObserver;
  private readonly container: HTMLElement;

  private isHeaderLogoIntersecting = false;
  private isFooterNavIntersecting = false;

  private readonly renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: false,
  });

  private readonly scene = new THREE.Scene();

  private readonly camera = new THREE.PerspectiveCamera(55, 1, 0.1, 300);

  private readonly timer = new THREE.Timer();

  private readonly starGeometry = new THREE.BufferGeometry();

  private readonly skyMaterial: THREE.ShaderMaterial;

  private readonly starMaterial: THREE.ShaderMaterial;

  private readonly gradientMaterial: THREE.ShaderMaterial;

  private readonly auroraMaterial: THREE.ShaderMaterial;

  private readonly sky: THREE.Mesh;

  private readonly stars: THREE.Points;

  private readonly gradient: THREE.Mesh;

  private readonly aurora: THREE.Mesh;

  private readonly auroraClone: THREE.Mesh;

  private readonly auroraCloneTwo: THREE.Mesh;

  private sceneTime = 0;

  private auroraTime = 0;

  private planesTranslateYProgress =
    defaultConfig.pageEdgeIntersection.translateYDefault;

  private gradientTranslateYProgress =
    defaultConfig.pageEdgeIntersection.translateYDefault;

  private planesTranslateYAnimation?: {
    startTime: number;
    from: number;
    to: number;
  };

  private gradientTranslateYAnimation?: {
    startTime: number;
    from: number;
    to: number;
  };

  private readonly gradientMouseTarget = new THREE.Vector2(0.5, 0.5);

  private readonly gradientMouseCurrent = new THREE.Vector2(0.5, 0.5);

  private animationFrameId?: number;

  constructor() {
    this.pageHeaderLogoIntersectionObserver = new IntersectionObserver(
      this.onPageHeaderLogoIntersection,
    );

    this.pageFooterNavIntersectionObserver = new IntersectionObserver(
      this.onPageFooterNavIntersection,
    );

    this.container = document.querySelector(
      ".aurora-canvas-container",
    ) as HTMLElement;

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.domElement.className = "aurora-canvas";
    this.container.prepend(this.renderer.domElement);

    const headerLogo = document.getElementById("header-logo");
    const footerNav = document.getElementById("footer-nav");

    if (headerLogo) {
      this.pageHeaderLogoIntersectionObserver.observe(headerLogo);
    }

    if (footerNav) {
      this.pageFooterNavIntersectionObserver.observe(footerNav);
    }

    this.skyMaterial = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      uniforms: {
        topColor: { value: new THREE.Color(this.params.sky.topColor) },
        bottomColor: { value: new THREE.Color(this.params.sky.bottomColor) },
        offsetY: { value: this.params.sky.offsetY },
      },
      vertexShader: `
        varying vec3 vWorldPosition;

        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPosition = worldPosition.xyz;
          gl_Position = projectionMatrix * viewMatrix * worldPosition;
        }
      `,
      fragmentShader: `
        varying vec3 vWorldPosition;
        uniform vec3 topColor;
        uniform vec3 bottomColor;
        uniform float offsetY;

        void main() {
          float h = normalize(vWorldPosition + vec3(0.0, offsetY, 0.0)).y * 0.5 + 0.5;
          vec3 color = mix(bottomColor, topColor, smoothstep(0.0, 1.0, h));
          gl_FragColor = vec4(color, 1.0);
        }
      `,
    });

    this.sky = new THREE.Mesh(
      new THREE.SphereGeometry(120, 48, 48),
      this.skyMaterial,
    );
    this.scene.add(this.sky);

    this.starMaterial = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uTwinkleBase: { value: this.params.stars.twinkleBase },
        uTwinkleRange: { value: this.params.stars.twinkleRange },
        uTwinkleSpeed: { value: this.params.stars.twinkleSpeed },
        uPointScale: { value: this.params.stars.pointScale },
        uColor: { value: new THREE.Color(this.params.stars.color) },
      },
      vertexShader: `
        attribute float aSize;
        varying float vAlpha;
        uniform float uTime;
        uniform float uTwinkleBase;
        uniform float uTwinkleRange;
        uniform float uTwinkleSpeed;
        uniform float uPointScale;

        void main() {
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          float twinkle = uTwinkleBase + uTwinkleRange * sin(uTime * uTwinkleSpeed + position.x * 12.0 + position.y);
          vAlpha = twinkle;
          gl_PointSize = aSize * twinkle * (uPointScale / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        varying float vAlpha;
        uniform vec3 uColor;

        void main() {
          vec2 centered = gl_PointCoord - 0.5;
          float d = length(centered);
          float strength = smoothstep(0.5, 0.0, d);
          gl_FragColor = vec4(uColor, strength * vAlpha);
        }
      `,
    });

    this.rebuildStars();
    this.stars = new THREE.Points(this.starGeometry, this.starMaterial);
    this.scene.add(this.stars);

    this.gradientMaterial = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      uniforms: {
        topColorA: { value: new THREE.Color(this.params.gradient.topColorA) },
        bottomColorA: {
          value: new THREE.Color(this.params.gradient.bottomColorA),
        },
        topColorB: { value: new THREE.Color(this.params.gradient.topColorB) },
        bottomColorB: {
          value: new THREE.Color(this.params.gradient.bottomColorB),
        },
        offsetY: { value: this.params.gradient.offsetY },
        opacity: { value: this.params.gradient.opacity },
        fadeBottomStart: { value: this.params.gradient.fadeBottomStart },
        fadeBottomEnd: { value: this.params.gradient.fadeBottomEnd },
        fadeTopStart: { value: this.params.gradient.fadeTopStart },
        fadeTopEnd: { value: this.params.gradient.fadeTopEnd },
        mousePosition: { value: this.gradientMouseCurrent.clone() },
      },
      vertexShader: `
        varying vec3 vLocalPosition;

        void main() {
          vLocalPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        varying vec3 vLocalPosition;
        uniform vec3 topColorA;
        uniform vec3 bottomColorA;
        uniform vec3 topColorB;
        uniform vec3 bottomColorB;
        uniform float offsetY;
        uniform float opacity;
        uniform float fadeBottomStart;
        uniform float fadeBottomEnd;
        uniform float fadeTopStart;
        uniform float fadeTopEnd;
        uniform vec2 mousePosition;

        void main() {
          float h = clamp((vLocalPosition.y + offsetY) / 24.0 + 0.5, 0.0, 1.0);
          float mouseMix = clamp(
            mousePosition.x * 0.5 + (1.0 - mousePosition.y) * 0.5,
            0.0,
            1.0
          );
          vec3 topColor = mix(topColorA, topColorB, mouseMix);
          vec3 bottomColor = mix(bottomColorA, bottomColorB, mouseMix);
          vec3 color = mix(bottomColor, topColor, smoothstep(0.0, 1.0, h));
          float bottomFade = smoothstep(fadeBottomStart, fadeBottomEnd, vLocalPosition.y);
          float topFade = 1.0 - smoothstep(fadeTopStart, fadeTopEnd, vLocalPosition.y);
          float edgeFade = bottomFade * topFade;
          gl_FragColor = vec4(color, opacity * edgeFade);
        }
      `,
    });

    this.gradient = new THREE.Mesh(
      new THREE.PlaneGeometry(90, 48, 1, 1),
      this.gradientMaterial,
    );
    this.gradient.position.set(0, this.params.gradient.translateY, -34);
    this.scene.add(this.gradient);

    this.auroraMaterial = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      uniforms: {
        uTime: { value: 0 },
        uColorA: { value: new THREE.Color(this.params.aurora.colorA) },
        uColorB: { value: new THREE.Color(this.params.aurora.colorB) },
        uColorC: { value: new THREE.Color(this.params.aurora.colorC) },
        uOpacity: { value: this.params.aurora.opacity },
        uColumns: { value: this.params.aurora.columns },
        uColumnStrength: { value: this.params.aurora.columnStrength },
        uDriftSpeed: { value: this.params.aurora.driftSpeed },
        uVeilScaleX: { value: this.params.aurora.veilScaleX },
        uVeilScaleY: { value: this.params.aurora.veilScaleY },
        uShimmerScaleX: { value: this.params.aurora.shimmerScaleX },
        uShimmerScaleY: { value: this.params.aurora.shimmerScaleY },
        uShimmerStrength: { value: this.params.aurora.shimmerStrength },
        uLowerFadeStart: { value: this.params.aurora.lowerFadeStart },
        uLowerFadeEnd: { value: this.params.aurora.lowerFadeEnd },
        uUpperFadeStart: { value: this.params.aurora.upperFadeStart },
        uUpperFadeEnd: { value: this.params.aurora.upperFadeEnd },
        uWave1Speed: { value: this.params.aurora.wave1Speed },
        uWave1Scale: { value: this.params.aurora.wave1Scale },
        uWave1Strength: { value: this.params.aurora.wave1Strength },
        uWave2Speed: { value: this.params.aurora.wave2Speed },
        uWave2Scale: { value: this.params.aurora.wave2Scale },
        uWave2Strength: { value: this.params.aurora.wave2Strength },
        uWave3Speed: { value: this.params.aurora.wave3Speed },
        uWave3Scale: { value: this.params.aurora.wave3Scale },
        uWave3Strength: { value: this.params.aurora.wave3Strength },
        uSwayAmplitude: { value: this.params.aurora.swayAmplitude },
        uSwayFrequency: { value: this.params.aurora.swayFrequency },
        uSwaySpeed: { value: this.params.aurora.swaySpeed },
        uRibbonStart: { value: this.params.aurora.ribbonStart },
        uRibbonPeak: { value: this.params.aurora.ribbonPeak },
        uRibbonEnd: { value: this.params.aurora.ribbonEnd },
        uRibbonTop: { value: this.params.aurora.ribbonTop },
        uEdgeFadeLeftStart: { value: this.params.aurora.edgeFadeLeftStart },
        uEdgeFadeLeftEnd: { value: this.params.aurora.edgeFadeLeftEnd },
        uEdgeFadeRightStart: { value: this.params.aurora.edgeFadeRightStart },
        uEdgeFadeRightEnd: { value: this.params.aurora.edgeFadeRightEnd },
        uEdgeFadeBottomStart: { value: this.params.aurora.edgeFadeBottomStart },
        uEdgeFadeBottomEnd: { value: this.params.aurora.edgeFadeBottomEnd },
        uEdgeFadeTopStart: { value: this.params.aurora.edgeFadeTopStart },
        uEdgeFadeTopEnd: { value: this.params.aurora.edgeFadeTopEnd },
      },
      vertexShader: `
        varying vec2 vUv;
        varying float vElevation;
        uniform float uTime;
        uniform float uWave1Speed;
        uniform float uWave1Scale;
        uniform float uWave1Strength;
        uniform float uWave2Speed;
        uniform float uWave2Scale;
        uniform float uWave2Strength;
        uniform float uWave3Speed;
        uniform float uWave3Scale;
        uniform float uWave3Strength;
        uniform float uSwayAmplitude;
        uniform float uSwayFrequency;
        uniform float uSwaySpeed;
        uniform float uRibbonStart;
        uniform float uRibbonPeak;
        uniform float uRibbonEnd;
        uniform float uRibbonTop;

        float wave(float value, float speed, float scale) {
          return sin(value * scale + uTime * speed);
        }

        void main() {
          vUv = uv;
          vec3 transformed = position;

          float ribbon = wave(position.x, uWave1Speed, uWave1Scale) * uWave1Strength;
          ribbon += wave(position.x + position.y, uWave2Speed, uWave2Scale) * uWave2Strength;
          ribbon += wave(position.x * 0.4 - position.y, uWave3Speed, uWave3Scale) * uWave3Strength;

          float lower = smoothstep(uRibbonStart, uRibbonPeak, uv.y);
          float upper = 1.0 - smoothstep(uRibbonEnd, uRibbonTop, uv.y);
          float falloff = lower * upper;

          transformed.z += ribbon * falloff;
          transformed.x += sin(uv.y * uSwayFrequency + uTime * uSwaySpeed) * uSwayAmplitude;

          vElevation = transformed.z;

          gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
        }
      `,
      fragmentShader: `
        varying vec2 vUv;
        varying float vElevation;
        uniform float uTime;
        uniform vec3 uColorA;
        uniform vec3 uColorB;
        uniform vec3 uColorC;
        uniform float uOpacity;
        uniform float uColumns;
        uniform float uColumnStrength;
        uniform float uDriftSpeed;
        uniform float uVeilScaleX;
        uniform float uVeilScaleY;
        uniform float uShimmerScaleX;
        uniform float uShimmerScaleY;
        uniform float uShimmerStrength;
        uniform float uLowerFadeStart;
        uniform float uLowerFadeEnd;
        uniform float uUpperFadeStart;
        uniform float uUpperFadeEnd;
        uniform float uEdgeFadeLeftStart;
        uniform float uEdgeFadeLeftEnd;
        uniform float uEdgeFadeRightStart;
        uniform float uEdgeFadeRightEnd;
        uniform float uEdgeFadeBottomStart;
        uniform float uEdgeFadeBottomEnd;
        uniform float uEdgeFadeTopStart;
        uniform float uEdgeFadeTopEnd;

        vec2 hash(vec2 p) {
          p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
          return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);
        }

        float noise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);

          vec2 ga = hash(i + vec2(0.0, 0.0));
          vec2 gb = hash(i + vec2(1.0, 0.0));
          vec2 gc = hash(i + vec2(0.0, 1.0));
          vec2 gd = hash(i + vec2(1.0, 1.0));

          vec2 u = f * f * (3.0 - 2.0 * f);

          float a = dot(ga, f - vec2(0.0, 0.0));
          float b = dot(gb, f - vec2(1.0, 0.0));
          float c = dot(gc, f - vec2(0.0, 1.0));
          float d = dot(gd, f - vec2(1.0, 1.0));

          return 0.5 + 0.5 * mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
        }

        void main() {
          vec2 uv = vUv;
          float drift = uTime * uDriftSpeed;
          float column = sin(uv.x * uColumns + drift * 10.0) * uColumnStrength + 0.5;
          vec2 veilUv = vec2(uv.x * uVeilScaleX - drift, uv.y * uVeilScaleY + drift * 0.4);
          vec2 warp = vec2(
            noise(veilUv * 0.6 + vec2(2.4, -1.7)),
            noise(veilUv * 0.6 + vec2(-3.1, 0.9))
          ) - 0.5;
          veilUv += warp * 0.35;
          float veil = noise(veilUv);

          vec2 shimmerUv = vec2(
            uv.x * uShimmerScaleX + drift * 1.8,
            uv.y * uShimmerScaleY - drift
          );
          shimmerUv += warp * 0.2;
          float shimmer = sin(shimmerUv.x + shimmerUv.y * 0.6);
          shimmer *= sin(shimmerUv.y * 1.7 - shimmerUv.x * 0.35);
          shimmer = shimmer * 0.5 + 0.5;
          float verticalFade = smoothstep(uLowerFadeStart, uLowerFadeEnd, uv.y) *
            (1.0 - smoothstep(uUpperFadeStart, uUpperFadeEnd, uv.y));
          float leftEdgeFade = smoothstep(uEdgeFadeLeftStart, uEdgeFadeLeftEnd, uv.x);
          float rightEdgeFade = 1.0 - smoothstep(uEdgeFadeRightStart, uEdgeFadeRightEnd, uv.x);
          float bottomEdgeFade = smoothstep(uEdgeFadeBottomStart, uEdgeFadeBottomEnd, uv.y);
          float topEdgeFade = 1.0 - smoothstep(uEdgeFadeTopStart, uEdgeFadeTopEnd, uv.y);
          float edgeFade = leftEdgeFade * rightEdgeFade * bottomEdgeFade * topEdgeFade;

          float alpha = column * veil * 0.9 + shimmer * uShimmerStrength;
          alpha *= verticalFade;
          alpha *= edgeFade;
          alpha *= 0.95 + vElevation * 0.08;

          vec3 color = mix(uColorA, uColorB, uv.x * 0.65 + shimmer * 0.25);
          color = mix(color, uColorC, pow(1.0 - uv.y, 2.0) * 0.45 + shimmer * 0.1);

          gl_FragColor = vec4(color, alpha * uOpacity);
        }
      `,
    });

    const auroraGeometry = new THREE.PlaneGeometry(42, 24, 60, 30);
    this.aurora = new THREE.Mesh(auroraGeometry, this.auroraMaterial);
    this.auroraClone = this.aurora.clone();
    this.auroraCloneTwo = this.aurora.clone();
    this.scene.add(this.aurora, this.auroraClone, this.auroraCloneTwo);

    this.applyAll();
    this.setupGui();
    this.gui.hide();
    this.onResize();
    window.addEventListener("resize", this.onResize);
    // window.addEventListener("beforeunload", this.destroy);
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("mousemove", this.onMouseMove);
    this.tick();
  }

  destroy = () => {
    if (this.animationFrameId !== undefined) {
      window.cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = undefined;
    }

    window.removeEventListener("resize", this.onResize);
    // window.removeEventListener("beforeunload", this.destroy);
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("mousemove", this.onMouseMove);
    this.importInput.removeEventListener("change", this.handleImportChange);
    this.pageHeaderLogoIntersectionObserver.disconnect();
    this.pageFooterNavIntersectionObserver.disconnect();
    this.gui.destroy();
    this.renderer.domElement.remove();
    this.starGeometry.dispose();
    this.sky.geometry.dispose();
    this.gradient.geometry.dispose();
    this.aurora.geometry.dispose();
    this.skyMaterial.dispose();
    this.starMaterial.dispose();
    this.gradientMaterial.dispose();
    this.auroraMaterial.dispose();
    this.renderer.dispose();
  };

  private assignDeep(
    target: Record<string, unknown>,
    source: Record<string, unknown>,
  ) {
    Object.entries(source).forEach(([key, value]) => {
      if (
        value !== null &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        typeof target[key] === "object" &&
        target[key] !== null &&
        !Array.isArray(target[key])
      ) {
        this.assignDeep(
          target[key] as Record<string, unknown>,
          value as Record<string, unknown>,
        );
        return;
      }

      target[key] = value;
    });
  }

  private refreshGui() {
    (
      this.gui as GUI & {
        controllersRecursive: () => Array<{ updateDisplay: () => void }>;
      }
    )
      .controllersRecursive()
      .forEach((controller) => controller.updateDisplay());
  }

  private exportConfig() {
    const json = JSON.stringify(this.params, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "aurora-config.json";
    link.click();
    URL.revokeObjectURL(url);
  }

  private handleImportChange = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.item(0);

    if (!file) {
      return;
    }

    const text = await file.text();

    try {
      const imported = JSON.parse(text) as Record<string, unknown>;
      this.assignDeep(this.params as Record<string, unknown>, imported);
      this.applyAll();
      this.refreshGui();
    } catch (error) {
      console.error("Failed to import config", error);
    } finally {
      target.value = "";
    }
  };

  private addPlaneFolder(title: string, config: AuroraPlaneConfig) {
    const folder = this.gui.addFolder(title);
    folder.add(config, "x", -40, 40, 0.1).onChange(this.applyAuroraPlanes);
    folder.add(config, "y", -20, 20, 0.1).onChange(this.applyAuroraPlanes);
    folder.add(config, "z", -60, 20, 0.1).onChange(this.applyAuroraPlanes);
    folder
      .add(config, "rotationX", -Math.PI, Math.PI, 0.01)
      .onChange(this.applyAuroraPlanes);
    folder
      .add(config, "rotationY", -Math.PI, Math.PI, 0.01)
      .onChange(this.applyAuroraPlanes);
    folder.add(config, "scaleX", 0.1, 4, 0.01).onChange(this.applyAuroraPlanes);
    folder.add(config, "scaleY", 0.1, 4, 0.01).onChange(this.applyAuroraPlanes);
  }

  private setupGui() {
    this.gui.domElement.setAttribute("data-lenis-prevent", "");

    this.importInput.type = "file";
    this.importInput.accept = "application/json";
    this.importInput.addEventListener("change", this.handleImportChange);

    const presetActions = {
      savePreset: () => {
        window.localStorage.setItem(
          PRESET_STORAGE_KEY,
          JSON.stringify(this.params),
        );
      },
      loadPreset: () => {
        const saved = window.localStorage.getItem(PRESET_STORAGE_KEY);

        if (!saved) {
          return;
        }

        try {
          const parsed = JSON.parse(saved) as Record<string, unknown>;
          this.assignDeep(this.params as Record<string, unknown>, parsed);
          this.applyAll();
          this.refreshGui();
        } catch (error) {
          console.error("Failed to load saved preset", error);
        }
      },
      resetDefaults: () => {
        this.assignDeep(
          this.params as Record<string, unknown>,
          structuredClone(defaultConfig) as Record<string, unknown>,
        );
        this.applyAll();
        this.refreshGui();
      },
      exportJson: () => {
        this.exportConfig();
      },
      importJson: () => {
        this.importInput.click();
      },
    };

    const presetFolder = this.gui.addFolder("Presets");
    presetFolder.add(presetActions, "savePreset");
    presetFolder.add(presetActions, "loadPreset");
    presetFolder.add(presetActions, "resetDefaults");
    presetFolder.add(presetActions, "exportJson");
    presetFolder.add(presetActions, "importJson");

    const cameraFolder = this.gui.addFolder("Camera");
    cameraFolder
      .add(this.params.camera, "fov", 20, 100, 1)
      .onChange(this.applyCamera);
    cameraFolder
      .add(this.params.camera, "x", -20, 20, 0.1)
      .onChange(this.applyCamera);
    cameraFolder
      .add(this.params.camera, "y", -10, 20, 0.1)
      .onChange(this.applyCamera);
    cameraFolder
      .add(this.params.camera, "z", 1, 60, 0.1)
      .onChange(this.applyCamera);
    cameraFolder.add(this.params.camera, "lookAtX", -30, 30, 0.1);
    cameraFolder.add(this.params.camera, "lookAtY", -20, 20, 0.1);
    cameraFolder.add(this.params.camera, "lookAtZ", -60, 20, 0.1);
    cameraFolder.add(this.params.camera, "driftX", 0, 5, 0.01);
    cameraFolder.add(this.params.camera, "driftY", 0, 2, 0.01);
    cameraFolder.add(this.params.camera, "driftSpeedX", 0, 1, 0.001);
    cameraFolder.add(this.params.camera, "driftSpeedY", 0, 1, 0.001);

    const skyFolder = this.gui.addFolder("Sky");
    skyFolder.addColor(this.params.sky, "topColor").onChange(this.applySky);
    skyFolder.addColor(this.params.sky, "bottomColor").onChange(this.applySky);
    skyFolder
      .add(this.params.sky, "offsetY", -100, 300, 0.1)
      .onChange(this.applySky);

    const gradientFolder = this.gui.addFolder("Aurora Gradient");
    gradientFolder
      .addColor(this.params.gradient, "topColorA")
      .onChange(this.applyGradient);
    gradientFolder
      .addColor(this.params.gradient, "bottomColorA")
      .onChange(this.applyGradient);
    gradientFolder
      .addColor(this.params.gradient, "topColorB")
      .onChange(this.applyGradient);
    gradientFolder
      .addColor(this.params.gradient, "bottomColorB")
      .onChange(this.applyGradient);
    gradientFolder
      .add(this.params.gradient, "offsetY", -40, 40, 0.1)
      .onChange(this.applyGradient);
    gradientFolder
      .add(this.params.gradient, "translateY", -20, 20, 0.1)
      .onChange(this.applyGradient);
    gradientFolder
      .add(this.params.gradient, "opacity", 0, 1, 0.01)
      .onChange(this.applyGradient);
    gradientFolder
      .add(this.params.gradient, "fadeBottomStart", -24, 24, 0.1)
      .onChange(this.applyGradient);
    gradientFolder
      .add(this.params.gradient, "fadeBottomEnd", -24, 24, 0.1)
      .onChange(this.applyGradient);
    gradientFolder
      .add(this.params.gradient, "fadeTopStart", -24, 24, 0.1)
      .onChange(this.applyGradient);
    gradientFolder
      .add(this.params.gradient, "fadeTopEnd", -24, 24, 0.1)
      .onChange(this.applyGradient);
    gradientFolder
      .add(this.params.gradient, "mouseMoveEase", 0.01, 1, 0.01)
      .onChange(this.applyGradient);

    const starFolder = this.gui.addFolder("Stars");
    starFolder
      .add(this.params.stars, "twinkleBase", 0, 2, 0.01)
      .onChange(this.applyStars);
    starFolder
      .add(this.params.stars, "twinkleRange", 0, 2, 0.01)
      .onChange(this.applyStars);
    starFolder
      .add(this.params.stars, "twinkleSpeed", 0, 8, 0.01)
      .onChange(this.applyStars);
    starFolder
      .add(this.params.stars, "pointScale", 10, 300, 1)
      .onChange(this.applyStars);
    starFolder.add(this.params.stars, "rotationSpeed", 0, 0.1, 0.001);
    starFolder
      .add(this.params.stars, "count", 100, 5000, 1)
      .onFinishChange(this.rebuildStars);
    starFolder.addColor(this.params.stars, "color").onChange(this.applyStars);

    const auroraFolder = this.gui.addFolder("Aurora Shader");
    auroraFolder
      .addColor(this.params.aurora, "colorA")
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .addColor(this.params.aurora, "colorB")
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .addColor(this.params.aurora, "colorC")
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "opacity", 0, 2, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder.add(this.params.aurora, "timeScale", 0, 4, 0.01);
    auroraFolder
      .add(this.params.aurora, "driftSpeed", 0, 1, 0.001)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "columns", 1, 60, 0.1)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "columnStrength", 0, 2, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "veilScaleX", 0.1, 40, 0.1)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "veilScaleY", 0.1, 40, 0.1)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "shimmerScaleX", 0.1, 30, 0.1)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "shimmerScaleY", 0.1, 30, 0.1)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "shimmerStrength", 0, 2, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "lowerFadeStart", 0, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "lowerFadeEnd", 0, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "upperFadeStart", 0, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "upperFadeEnd", 0, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "wave1Speed", 0, 3, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "wave1Scale", 0.01, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "wave1Strength", 0, 10, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "wave2Speed", 0, 3, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "wave2Scale", 0.01, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "wave2Strength", 0, 10, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "wave3Speed", 0, 3, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "wave3Scale", 0.01, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "wave3Strength", 0, 10, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "swayAmplitude", 0, 4, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "swayFrequency", 0, 20, 0.1)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "swaySpeed", 0, 4, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "ribbonStart", 0, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "ribbonPeak", 0, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "ribbonEnd", 0, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "ribbonTop", 0, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "edgeFadeLeftStart", 0, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "edgeFadeLeftEnd", 0, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "edgeFadeRightStart", 0, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "edgeFadeRightEnd", 0, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "edgeFadeBottomStart", 0, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "edgeFadeBottomEnd", 0, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "edgeFadeTopStart", 0, 1, 0.01)
      .onChange(this.applyAuroraMaterial);
    auroraFolder
      .add(this.params.aurora, "edgeFadeTopEnd", 0, 1, 0.01)
      .onChange(this.applyAuroraMaterial);

    const pageEdgeIntersectionFolder = this.gui.addFolder("State translate Y");
    pageEdgeIntersectionFolder
      .add(this.params.pageEdgeIntersection, "translateYTop", -320, 320, 1)
      .name("top")
      .onChange(this.onStateTranslateYConfigChange);
    pageEdgeIntersectionFolder
      .add(this.params.pageEdgeIntersection, "translateYDefault", -320, 320, 1)
      .name("default")
      .onChange(this.onStateTranslateYConfigChange);
    pageEdgeIntersectionFolder
      .add(this.params.pageEdgeIntersection, "translateYBottom", -320, 320, 1)
      .name("bottom")
      .onChange(this.onStateTranslateYConfigChange);

    this.addPlaneFolder("Plane A", this.params.planes.planeA);
    this.addPlaneFolder("Plane B", this.params.planes.planeB);
    this.addPlaneFolder("Plane C", this.params.planes.planeC);
  }

  private rebuildStars = () => {
    const starPositions = new Float32Array(this.params.stars.count * 3);
    const starSizes = new Float32Array(this.params.stars.count);

    for (let index = 0; index < this.params.stars.count; index += 1) {
      const i3 = index * 3;
      const radius =
        this.params.stars.minRadius +
        Math.random() *
          (this.params.stars.maxRadius - this.params.stars.minRadius);
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI * 0.55;

      starPositions[i3] = Math.cos(theta) * Math.sin(phi) * radius;
      starPositions[i3 + 1] =
        this.params.stars.verticalOffset +
        Math.cos(phi) * radius * this.params.stars.heightSpread;
      starPositions[i3 + 2] =
        Math.sin(theta) * Math.sin(phi) * radius +
        this.params.stars.depthOffset;
      starSizes[index] =
        this.params.stars.minSize +
        Math.random() * (this.params.stars.maxSize - this.params.stars.minSize);
    }

    this.starGeometry.setAttribute(
      "position",
      new THREE.BufferAttribute(starPositions, 3),
    );
    this.starGeometry.setAttribute(
      "aSize",
      new THREE.BufferAttribute(starSizes, 1),
    );
  };

  private applyCamera = () => {
    this.camera.fov = this.params.camera.fov;
    this.camera.position.set(
      this.params.camera.x,
      this.params.camera.y,
      this.params.camera.z,
    );
    this.camera.updateProjectionMatrix();
  };

  private applySky = () => {
    this.skyMaterial.uniforms.topColor.value.set(this.params.sky.topColor);
    this.skyMaterial.uniforms.bottomColor.value.set(
      this.params.sky.bottomColor,
    );
    this.skyMaterial.uniforms.offsetY.value = this.params.sky.offsetY;
  };

  private applyStars = () => {
    this.starMaterial.uniforms.uTwinkleBase.value =
      this.params.stars.twinkleBase;
    this.starMaterial.uniforms.uTwinkleRange.value =
      this.params.stars.twinkleRange;
    this.starMaterial.uniforms.uTwinkleSpeed.value =
      this.params.stars.twinkleSpeed;
    this.starMaterial.uniforms.uPointScale.value = this.params.stars.pointScale;
    this.starMaterial.uniforms.uColor.value.set(this.params.stars.color);
  };

  private applyGradient = () => {
    this.gradientMaterial.uniforms.topColorA.value.set(
      this.params.gradient.topColorA,
    );
    this.gradientMaterial.uniforms.bottomColorA.value.set(
      this.params.gradient.bottomColorA,
    );
    this.gradientMaterial.uniforms.topColorB.value.set(
      this.params.gradient.topColorB,
    );
    this.gradientMaterial.uniforms.bottomColorB.value.set(
      this.params.gradient.bottomColorB,
    );
    this.gradientMaterial.uniforms.offsetY.value = this.params.gradient.offsetY;
    this.gradientMaterial.uniforms.opacity.value = this.params.gradient.opacity;
    this.gradientMaterial.uniforms.fadeBottomStart.value =
      this.params.gradient.fadeBottomStart;
    this.gradientMaterial.uniforms.fadeBottomEnd.value =
      this.params.gradient.fadeBottomEnd;
    this.gradientMaterial.uniforms.fadeTopStart.value =
      this.params.gradient.fadeTopStart;
    this.gradientMaterial.uniforms.fadeTopEnd.value =
      this.params.gradient.fadeTopEnd;
    this.gradient.position.y =
      this.params.gradient.translateY + this.getStateGradientYOffset();
  };

  private onMouseMove = (event: MouseEvent) => {
    const width = window.innerWidth || 1;
    const height = window.innerHeight || 1;
    this.gradientMouseTarget.set(
      THREE.MathUtils.clamp(event.clientX / width, 0, 1),
      THREE.MathUtils.clamp(event.clientY / height, 0, 1),
    );
  };

  private applyAuroraMaterial = () => {
    this.auroraMaterial.uniforms.uColorA.value.set(this.params.aurora.colorA);
    this.auroraMaterial.uniforms.uColorB.value.set(this.params.aurora.colorB);
    this.auroraMaterial.uniforms.uColorC.value.set(this.params.aurora.colorC);
    this.auroraMaterial.uniforms.uOpacity.value = this.params.aurora.opacity;
    this.auroraMaterial.uniforms.uColumns.value = this.params.aurora.columns;
    this.auroraMaterial.uniforms.uColumnStrength.value =
      this.params.aurora.columnStrength;
    this.auroraMaterial.uniforms.uDriftSpeed.value =
      this.params.aurora.driftSpeed;
    this.auroraMaterial.uniforms.uVeilScaleX.value =
      this.params.aurora.veilScaleX;
    this.auroraMaterial.uniforms.uVeilScaleY.value =
      this.params.aurora.veilScaleY;
    this.auroraMaterial.uniforms.uShimmerScaleX.value =
      this.params.aurora.shimmerScaleX;
    this.auroraMaterial.uniforms.uShimmerScaleY.value =
      this.params.aurora.shimmerScaleY;
    this.auroraMaterial.uniforms.uShimmerStrength.value =
      this.params.aurora.shimmerStrength;
    this.auroraMaterial.uniforms.uLowerFadeStart.value =
      this.params.aurora.lowerFadeStart;
    this.auroraMaterial.uniforms.uLowerFadeEnd.value =
      this.params.aurora.lowerFadeEnd;
    this.auroraMaterial.uniforms.uUpperFadeStart.value =
      this.params.aurora.upperFadeStart;
    this.auroraMaterial.uniforms.uUpperFadeEnd.value =
      this.params.aurora.upperFadeEnd;
    this.auroraMaterial.uniforms.uWave1Speed.value =
      this.params.aurora.wave1Speed;
    this.auroraMaterial.uniforms.uWave1Scale.value =
      this.params.aurora.wave1Scale;
    this.auroraMaterial.uniforms.uWave1Strength.value =
      this.params.aurora.wave1Strength;
    this.auroraMaterial.uniforms.uWave2Speed.value =
      this.params.aurora.wave2Speed;
    this.auroraMaterial.uniforms.uWave2Scale.value =
      this.params.aurora.wave2Scale;
    this.auroraMaterial.uniforms.uWave2Strength.value =
      this.params.aurora.wave2Strength;
    this.auroraMaterial.uniforms.uWave3Speed.value =
      this.params.aurora.wave3Speed;
    this.auroraMaterial.uniforms.uWave3Scale.value =
      this.params.aurora.wave3Scale;
    this.auroraMaterial.uniforms.uWave3Strength.value =
      this.params.aurora.wave3Strength;
    this.auroraMaterial.uniforms.uSwayAmplitude.value =
      this.params.aurora.swayAmplitude;
    this.auroraMaterial.uniforms.uSwayFrequency.value =
      this.params.aurora.swayFrequency;
    this.auroraMaterial.uniforms.uSwaySpeed.value =
      this.params.aurora.swaySpeed;
    this.auroraMaterial.uniforms.uRibbonStart.value =
      this.params.aurora.ribbonStart;
    this.auroraMaterial.uniforms.uRibbonPeak.value =
      this.params.aurora.ribbonPeak;
    this.auroraMaterial.uniforms.uRibbonEnd.value =
      this.params.aurora.ribbonEnd;
    this.auroraMaterial.uniforms.uRibbonTop.value =
      this.params.aurora.ribbonTop;
    this.auroraMaterial.uniforms.uEdgeFadeLeftStart.value =
      this.params.aurora.edgeFadeLeftStart;
    this.auroraMaterial.uniforms.uEdgeFadeLeftEnd.value =
      this.params.aurora.edgeFadeLeftEnd;
    this.auroraMaterial.uniforms.uEdgeFadeRightStart.value =
      this.params.aurora.edgeFadeRightStart;
    this.auroraMaterial.uniforms.uEdgeFadeRightEnd.value =
      this.params.aurora.edgeFadeRightEnd;
    this.auroraMaterial.uniforms.uEdgeFadeBottomStart.value =
      this.params.aurora.edgeFadeBottomStart;
    this.auroraMaterial.uniforms.uEdgeFadeBottomEnd.value =
      this.params.aurora.edgeFadeBottomEnd;
    this.auroraMaterial.uniforms.uEdgeFadeTopStart.value =
      this.params.aurora.edgeFadeTopStart;
    this.auroraMaterial.uniforms.uEdgeFadeTopEnd.value =
      this.params.aurora.edgeFadeTopEnd;
  };

  private onPageHeaderLogoIntersection = (
    entries: IntersectionObserverEntry[],
  ) => {
    entries.forEach((entry) => {
      this.isHeaderLogoIntersecting = entry.isIntersecting;
    });

    this.syncStateTranslateYFromIntersection();
  };

  private onPageFooterNavIntersection = (
    entries: IntersectionObserverEntry[],
  ) => {
    entries.forEach((entry) => {
      this.isFooterNavIntersecting = entry.isIntersecting;
    });

    this.syncStateTranslateYFromIntersection();
  };

  private onStateTranslateYConfigChange = () => {
    this.animateStateTranslateY(this.params.pageEdgeIntersection.state);
  };

  private syncStateTranslateYFromIntersection() {
    if (this.isHeaderLogoIntersecting) {
      this.params.pageEdgeIntersection.state = "top";
      this.applyStateContainerStyles("top");
      this.animateStateTranslateY("top");
      return;
    }

    if (this.isFooterNavIntersecting) {
      this.params.pageEdgeIntersection.state = "bottom";
      this.applyStateContainerStyles("bottom");
      this.animateStateTranslateY("bottom");
      return;
    }

    this.params.pageEdgeIntersection.state = "default";
    this.applyStateContainerStyles("default");
    this.animateStateTranslateY("default");
  }

  private applyStateContainerStyles(state: StateTranslateY) {
    this.container.style.setProperty(
      "--fade-opacity",
      state === "default" ? "0.3" : "0",
    );
  }

  private getStateTranslateYTarget(state: StateTranslateY) {
    switch (state) {
      case "top":
        return this.params.pageEdgeIntersection.translateYTop;
      case "bottom":
        return this.params.pageEdgeIntersection.translateYBottom;
      case "default":
      default:
        return this.params.pageEdgeIntersection.translateYDefault;
    }
  }

  private animateStateTranslateY(state: StateTranslateY) {
    const target = this.getStateTranslateYTarget(state);
    const hasMatchingPlaneAnimation =
      this.planesTranslateYAnimation?.to === target ||
      (!this.planesTranslateYAnimation &&
        this.planesTranslateYProgress === target);

    if (!hasMatchingPlaneAnimation) {
      this.planesTranslateYAnimation = {
        startTime: performance.now(),
        from: this.planesTranslateYProgress,
        to: target,
      };
    }

    if (
      this.gradientTranslateYAnimation?.to === target ||
      (!this.gradientTranslateYAnimation &&
        this.gradientTranslateYProgress === target)
    ) {
      return;
    }

    this.gradientTranslateYAnimation = {
      startTime: performance.now(),
      from: this.gradientTranslateYProgress,
      to: target,
    };
  }

  private updatePlanesTranslateY(now: number) {
    if (this.planesTranslateYAnimation) {
      const elapsed = now - this.planesTranslateYAnimation.startTime;
      const progress = THREE.MathUtils.clamp(
        elapsed / HEADER_LOGO_PLANES_TRANSLATE_DURATION_MS,
        0,
        1,
      );
      const easedProgress = easeInOutSine(progress);

      this.planesTranslateYProgress = THREE.MathUtils.lerp(
        this.planesTranslateYAnimation.from,
        this.planesTranslateYAnimation.to,
        easedProgress,
      );

      if (progress >= 1) {
        this.planesTranslateYProgress = this.planesTranslateYAnimation.to;
        this.planesTranslateYAnimation = undefined;
      }
    }

    if (this.gradientTranslateYAnimation) {
      const elapsed = now - this.gradientTranslateYAnimation.startTime;
      const progress = THREE.MathUtils.clamp(
        elapsed / HEADER_LOGO_GRADIENT_TRANSLATE_DURATION_MS,
        0,
        1,
      );
      const easedProgress = easeInOutSine(progress);

      this.gradientTranslateYProgress = THREE.MathUtils.lerp(
        this.gradientTranslateYAnimation.from,
        this.gradientTranslateYAnimation.to,
        easedProgress,
      );

      if (progress >= 1) {
        this.gradientTranslateYProgress = this.gradientTranslateYAnimation.to;
        this.gradientTranslateYAnimation = undefined;
      }
    }
  }

  private getStateGradientYOffset() {
    return -this.gradientTranslateYProgress;
  }

  private getStatePlanesYOffset() {
    return -this.planesTranslateYProgress;
  }

  private applyPageEdgeIntersectionTransforms = () => {
    this.applyGradient();
    this.applyAuroraPlanes();
  };

  private applyPlane(mesh: THREE.Mesh, config: AuroraPlaneConfig) {
    mesh.position.set(
      config.x,
      config.y + this.getStatePlanesYOffset(),
      config.z,
    );
    mesh.rotation.set(config.rotationX, config.rotationY, 0);
    mesh.scale.set(config.scaleX, config.scaleY, 1);
  }

  private applyAuroraPlanes = () => {
    this.applyPlane(this.aurora, this.params.planes.planeA);
    this.applyPlane(this.auroraClone, this.params.planes.planeB);
    this.applyPlane(this.auroraCloneTwo, this.params.planes.planeC);
  };

  private applyAll() {
    this.applyStateContainerStyles(this.params.pageEdgeIntersection.state);
    this.rebuildStars();
    this.applyCamera();
    this.applySky();
    this.applyStars();
    this.applyGradient();
    this.applyAuroraMaterial();
    this.applyAuroraPlanes();
  }

  private onResize = () => {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  };

  private onKeyDown = (event: KeyboardEvent) => {
    if (!event.shiftKey || event.key.toLowerCase() !== "c") {
      return;
    }

    if (this.gui._hidden) {
      this.gui.show();
      return;
    }

    this.gui.hide();
  };

  private tick = () => {
    this.timer.update();
    this.updatePlanesTranslateY(performance.now());
    const dt = this.timer.getDelta() * 1000;
    const k = dt / TARGET_FRAME_MS;
    const normalizedDelta = k / TARGET_FPS;

    this.sceneTime += normalizedDelta;
    this.auroraTime += normalizedDelta * this.params.aurora.timeScale;

    this.auroraMaterial.uniforms.uTime.value = this.auroraTime;
    this.starMaterial.uniforms.uTime.value = this.sceneTime;
    this.gradientMouseCurrent.lerp(
      this.gradientMouseTarget,
      this.params.gradient.mouseMoveEase,
    );
    (this.gradientMaterial.uniforms.mousePosition.value as THREE.Vector2).copy(
      this.gradientMouseCurrent,
    );

    this.camera.position.x =
      this.params.camera.x +
      Math.sin(this.sceneTime * this.params.camera.driftSpeedX) *
        this.params.camera.driftX;
    this.camera.position.y =
      this.params.camera.y +
      Math.sin(this.sceneTime * this.params.camera.driftSpeedY) *
        this.params.camera.driftY;
    this.camera.lookAt(
      this.params.camera.lookAtX,
      this.params.camera.lookAtY,
      this.params.camera.lookAtZ,
    );

    this.stars.rotation.y = this.sceneTime * this.params.stars.rotationSpeed;
    this.applyPageEdgeIntersectionTransforms();

    this.renderer.autoClear = true;
    this.renderer.render(this.scene, this.camera);

    this.animationFrameId = window.requestAnimationFrame(this.tick);
  };
}
